import { SOLUTION_IMAGES } from './images'

export const SOLUTIONS = [
  {
    slug: 'commercial-pest-control',
    title: 'Commercial Pest Control',
    image: SOLUTION_IMAGES.commercialPestControl,
    shortDescription: 'Structured programs for food processing and audited facilities focused on audit readiness and verifiable documentation.',
    hero: 'Controlled Environments Require Controlled Programs',
    intro: 'In food processing facilities, a strong pest program is a critical part of maintaining product integrity, passing audits, and protecting operations. Aegis Professional Services delivers structured programs designed to maintain control, support continuous audit readiness, provide clear documentation, and respond immediately when conditions change.',
    sections: [
      {
        title: 'Routine Inspection & Monitoring',
        paragraphs: ['Every service includes a full inspection of interior and exterior areas, with focus on production and packaging zones, drains, dock doors, waste handling, and exterior perimeters.'],
        bullets: [
          'Insect Light Traps (ILTs) positioned in critical areas',
          'Interior and exterior rodent devices',
          'Pheromone devices used to track crawling insects',
          'Activity levels recorded and trended over time',
        ],
      },
      {
        title: 'Targeted Control Measures',
        paragraphs: ['Control actions are based on actual findings, not assumptions. Treatments are precisely applied to identified activity areas with methods selected based on pest type and facility conditions.'],
      },
      {
        title: 'Prevention & Risk Reduction',
        bullets: [
          'Moisture and drainage issues',
          'Sanitation gaps',
          'Structural access points',
          'Operational practices that contribute to activity',
        ],
      },
      {
        title: 'Documentation & Audit Readiness',
        paragraphs: ['All services are documented in a clear, structured format designed for audit visibility. Programs are aligned to support facilities operating under FDA/USDA oversight, FSMA expectations, and third-party audit standards.'],
      },
    ],
  },
  {
    slug: 'rodent-control',
    title: 'Rodent Control',
    image: SOLUTION_IMAGES.rodentControl,
    imagePosition: 'center 75%',
    shortDescription: 'Structured rodent management focused on monitoring, containment, and prevention in commercial and regulated environments.',
    hero: 'Rodent control programs built to contain activity, eliminate risk, and maintain control.',
    intro: 'APS delivers structured rodent management designed for commercial and regulated environments focused on consistency, visibility, and long-term stability.',
    sections: [
      {
        title: 'Operating Context',
        bullets: [
          'Product integrity',
          'Facility infrastructure',
          'Regulatory compliance',
          'Operational continuity',
        ],
        paragraphs: ['Rodents are adaptive and persistent. Effective control requires continuous monitoring, defined control methods, and disciplined execution.'],
      },
      {
        title: 'How the Service Is Delivered',
        bullets: [
          'Full inspection of interior activity zones and exterior conditions',
          'Mechanical trapping with strategic placement and ongoing monitoring',
          'Secured, tamper-resistant exterior bait stations',
          'Ongoing monitoring and activity tracking',
          'Prevention and condition management recommendations',
        ],
      },
      {
        title: 'Program Structure',
        bullets: [
          'Routine inspection at every service',
          'Monitoring of traps and stations',
          'Documented activity tracking',
          'Immediate escalation for elevated conditions',
        ],
      },
    ],
  },
  {
    slug: 'ant-control',
    title: 'Ant Control',
    image: SOLUTION_IMAGES.antControl,
    shortDescription: 'Targeted ant management focused on identification, source control, and ongoing monitoring.',
    hero: 'Ant control programs built to identify the source, eliminate activity, and prevent return.',
    intro: 'APS delivers structured ant management designed for commercial environments focused on consistency, visibility, and long-term control.',
    sections: [
      {
        title: 'Operating Context',
        paragraphs: ['Ant activity in commercial facilities is often persistent because colonies are hidden within walls, slabs, or exterior structures, supported by moisture and food sources, and able to re-establish quickly if not fully addressed.'],
      },
      {
        title: 'Identification & Assessment',
        bullets: ['Ant species', 'Nesting locations (interior or exterior)', 'Entry points and activity paths', 'Environmental contributors'],
      },
      {
        title: 'Targeted Control Measures',
        bullets: [
          'Baits and treatments specific to species and behavior',
          'Interior and exterior applications as required',
          'Focus on eliminating the colony, not just visible activity',
        ],
      },
      {
        title: 'Program Structure',
        bullets: [
          'Routine inspection of active and high-risk areas',
          'Monitoring of activity levels and patterns',
          'Adjustment of treatments based on results',
          'Immediate response if activity persists or increases',
        ],
      },
    ],
  },
  {
    slug: 'cockroach-control',
    title: 'Cockroach Control',
    image: SOLUTION_IMAGES.cockroachControl,
    imagePosition: 'center 75%',
    shortDescription: 'Structured commercial cockroach eradication with verification, monitoring, and accountability.',
    hero: 'Restoring Control Through Structured Execution',
    intro: 'Cockroach activity in a commercial facility requires more than treatment. It requires disciplined execution, verification, and accountability. This is a hands-on, structured eradication effort, not a routine service.',
    sections: [
      {
        title: 'Comprehensive Initial Assessment',
        bullets: [
          'Identifying species and infestation type',
          'Locating primary and secondary harborages',
          'Evaluating spread across production, storage, and support areas',
          'Inspecting drains, equipment voids, and sanitation zones',
        ],
      },
      {
        title: 'Targeted Elimination Effort',
        bullets: [
          'Strategic baiting systems within active harborages',
          'Crack and crevice treatments in concealed areas',
          'Targeted applications in voids and structural gaps',
          'Drain-focused treatments where moisture contributes',
        ],
      },
      {
        title: 'Monitoring & Verification',
        paragraphs: ['Eradication is not assumed—it is verified through glue boards, monitoring devices, and ongoing activity tracking at each service visit.'],
      },
    ],
  },
  {
    slug: 'bird-control',
    title: 'Bird Control',
    image: SOLUTION_IMAGES.birdControl,
    imagePosition: 'center 20%',
    shortDescription: 'Bird management programs designed to prevent nesting, reduce contamination risk, and protect facility operations.',
    hero: 'Bird control programs that protect your facility, your assets, and your operating environment.',
    intro: 'APS provides structured bird management programs focused on deterrence, exclusion, and ongoing control — a site-specific program designed to reduce risk, protect assets, and maintain operational standards.',
    sections: [
      {
        title: 'Operating Context',
        bullets: [
          'Surface contamination from droppings',
          'Product and equipment exposure',
          'Slip hazards and sanitation concerns',
          'Structural damage from nesting',
          'Increased audit and compliance risk',
        ],
      },
      {
        title: 'How the Service Is Delivered',
        bullets: [
          'Inspection and activity assessment',
          'Physical deterrents and structural modifications',
          'Exclusion measures to seal or block access points',
          'Cleaning and condition correction when needed',
        ],
      },
      {
        title: 'Program Structure',
        bullets: [
          'Routine inspection of affected and high-risk areas',
          'Monitoring of deterrent system performance',
          'Maintenance and adjustment of installed systems',
          'Immediate response if activity increases',
        ],
      },
    ],
  },
  {
    slug: 'snake-control',
    title: 'Snake Control',
    image: SOLUTION_IMAGES.snakeControl,
    shortDescription: 'Snake deterrence and removal focused on habitat reduction, perimeter control, and safe handling.',
    hero: 'Snake control programs designed to reduce risk and maintain a controlled environment.',
    intro: 'APS provides structured snake management services focused on deterrence, environmental control, and safe removal supporting facility safety and operational continuity.',
    sections: [
      {
        title: 'Operating Context',
        bullets: [
          'Available shelter and cover',
          'Food sources such as rodents',
          'Structural access points',
          'Perimeter conditions and surrounding landscape',
        ],
      },
      {
        title: 'How the Service Is Delivered',
        bullets: [
          'Inspection and risk assessment',
          'Habitat reduction and overgrowth management',
          'Perimeter-focused deterrent strategies',
          'Safe removal in accordance with local regulations',
        ],
      },
    ],
  },
  {
    slug: 'wildlife-control',
    title: 'Wildlife Control',
    image: SOLUTION_IMAGES.wildlifeControl,
    imagePosition: 'center 25%',
    hero: 'Wildlife control programs built to deter intrusion, manage activity, and maintain a controlled environment.',
    intro: 'APS provides structured wildlife management services focused on prevention, controlled removal, and long-term stability across commercial and industrial environments.',
    sections: [
      {
        title: 'Operating Context',
        bullets: [
          'Structural damage and contamination risks',
          'Operational disruption',
          'Safety concerns for personnel',
          'Shelter opportunities and unsecured access points',
        ],
      },
      {
        title: 'How the Service Is Delivered',
        bullets: [
          'Inspection and activity assessment',
          'Physical deterrents and environmental adjustments',
          'Humane live trapping when wildlife is present',
          'Habitat and condition management',
        ],
      },
    ],
  },
]

export function getSolutionBySlug(slug) {
  return SOLUTIONS.find((solution) => solution.slug === slug)
}
